import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { InfoOutlined, TouchApp } from '@mui/icons-material';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { TONES } from '../../../components/ui/PageKit';
import type { PlantLayout, PlantZone } from './layouts';
import type { ZoneStatus } from './data';
import { ACCENT, INK, MUTED, PulseDot } from './ui';

interface Props {
  layout: PlantLayout;
  statuses: Record<string, ZoneStatus>;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Alto máximo del plano (CSS); el ancho sale de la proporción de la imagen. */
  maxHeight?: string;
}

const pct = (v: number, total: number) => `${(v / total) * 100}%`;

/** Plano de la planta con los sectores tocables encima. */
const PlantMap: React.FC<Props> = ({ layout, statuses, selected, onSelect, maxHeight }) => {
  const reduce = useReducedMotion();
  const [hover, setHover] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { width: W, height: H } = layout;
  const hovered = layout.zones.find(z => z.id === hover && z.id !== selected);
  const sel = layout.zones.find(z => z.id === selected);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        sx={{
          position: 'relative', width: maxHeight ? `min(100%, calc(${maxHeight} * ${W / H}))` : '100%',
          aspectRatio: `${W} / ${H}`, userSelect: 'none',
        }}
        onMouseLeave={() => setHover(null)}
      >
        <motion.img
          src={layout.image}
          alt={`Plano de la planta ${layout.name}`}
          onLoad={() => setLoaded(true)}
          initial={reduce ? false : { opacity: 0, scale: 1.015 }}
          animate={{ opacity: loaded ? 1 : 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', borderRadius: 12 }}
          draggable={false}
        />

        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
          <defs>
            <filter id="zone-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor={ACCENT} floodOpacity="0.55" />
            </filter>
          </defs>

          {/* Recorrido de entrada: los sectores se dibujan uno tras otro y se apagan. */}
          {loaded && !reduce && layout.zones.map((z, i) => (
            <motion.rect
              key={`intro-${z.id}`}
              x={z.box[0]} y={z.box[1]} width={z.box[2]} height={z.box[3]} rx={8}
              fill="none" stroke={ACCENT} strokeWidth={3} pointerEvents="none"
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: [0, 0.9, 0], pathLength: [0, 1, 1] }}
              transition={{ duration: 1.3, delay: 0.25 + i * 0.045, ease: 'easeInOut' }}
            />
          ))}

          {layout.zones.map(z => {
            const st = statuses[z.id];
            const isSel = z.id === selected;
            const isHover = z.id === hover && !isSel;
            const alarm = st?.tone === 'bad';
            return (
              <g key={z.id}>
                {/* Latido suave en máquinas produciendo y sectores en rojo. */}
                {loaded && !reduce && !isSel && (st?.active || alarm) && (
                  <motion.rect
                    x={z.box[0] - 3} y={z.box[1] - 3} width={z.box[2] + 6} height={z.box[3] + 6} rx={10}
                    fill="none" stroke={TONES[alarm ? 'bad' : 'ok'].dot} strokeWidth={3} pointerEvents="none"
                    animate={{ opacity: [0.15, 0.75, 0.15] }}
                    transition={{ duration: alarm ? 1.4 : 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
                <motion.rect
                  role="button"
                  tabIndex={0}
                  aria-label={`${z.label}: ${st?.label ?? ''}`}
                  aria-pressed={isSel}
                  x={z.box[0]} y={z.box[1]} width={z.box[2]} height={z.box[3]} rx={8}
                  style={{ cursor: 'pointer', outline: 'none' }}
                  filter={isSel ? 'url(#zone-glow)' : undefined}
                  initial={false}
                  animate={{
                    fill: isSel ? 'rgba(38,40,46,0.34)' : isHover ? 'rgba(38,40,46,0.16)' : 'rgba(0,0,0,0)',
                    stroke: isSel || isHover ? ACCENT : 'rgba(240,86,29,0)',
                    strokeWidth: isSel ? 5 : 3,
                  }}
                  transition={{ duration: 0.18 }}
                  onMouseEnter={() => setHover(z.id)}
                  onFocus={() => setHover(z.id)}
                  onBlur={() => setHover(h => (h === z.id ? null : h))}
                  onClick={() => onSelect(z.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(z.id); } }}
                />
              </g>
            );
          })}
        </svg>

        {/* Puntos de estado en la esquina de cada sector. */}
        {layout.zones.map((z, i) => {
          const st = statuses[z.id];
          if (!st || z.kind === 'soon') return null;
          return (
            <Box
              key={`dot-${z.id}`}
              sx={{
                position: 'absolute', left: pct(z.box[0] + z.box[2] - 10, W), top: pct(z.box[1] + 10, H),
                transform: 'translate(-50%, -50%)', pointerEvents: 'none',
              }}
            >
              <motion.div
                initial={reduce ? false : { opacity: 0, scale: 0 }}
                animate={{ opacity: loaded ? 1 : 0, scale: 1 }}
                transition={{ delay: 0.6 + i * 0.03, type: 'spring', stiffness: 420, damping: 22 }}
                style={{ display: 'flex' }}
              >
                <PulseDot tone={st.tone} size={11} pulse={st.tone === 'bad' || !!st.active} />
              </motion.div>
            </Box>
          );
        })}

        <AnimatePresence>
          {hovered && <ZoneTooltip key={hovered.id} zone={hovered} status={statuses[hovered.id]} W={W} H={H} />}
        </AnimatePresence>
        <AnimatePresence>
          {sel && (
            <motion.div
              key={`sel-${sel.id}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                position: 'absolute', left: pct(Math.min(sel.box[0] + sel.box[2] + 8, W - 200), W), top: pct(sel.box[1] + sel.box[3] / 2, H),
                pointerEvents: 'none', zIndex: 2,
              }}
            >
              <Box sx={{ transform: 'translateY(-50%)', bgcolor: '#fff', borderRadius: 1.5, px: 1.25, py: 0.5, boxShadow: '0 6px 20px rgba(0,0,0,.18)', whiteSpace: 'nowrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>{sel.label} · Seleccionado</Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, color: MUTED, alignSelf: 'flex-start' }}>
        <InfoOutlined sx={{ fontSize: 18 }} />
        <Typography variant="body2">Tocá un sector para ver su información</Typography>
        <TouchApp sx={{ fontSize: 18, display: { xs: 'inline', md: 'none' } }} />
      </Stack>
    </Box>
  );
};

const ZoneTooltip: React.FC<{ zone: PlantZone; status?: ZoneStatus; W: number; H: number }> = ({ zone, status, W, H }) => {
  const above = zone.box[1] > 120;
  const cx = Math.min(Math.max(zone.box[0] + zone.box[2] / 2, 120), W - 120);
  return (
    <Box sx={{
      position: 'absolute', left: pct(cx, W), top: pct(above ? zone.box[1] - 8 : zone.box[1] + zone.box[3] + 8, H),
      transform: `translate(-50%, ${above ? '-100%' : '0'})`, pointerEvents: 'none', zIndex: 3,
    }}>
    <motion.div
      initial={{ opacity: 0, y: above ? 6 : -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      transition={{ duration: 0.16 }}
    >
      <Box sx={{ bgcolor: INK, color: '#fff', borderRadius: 2, px: 1.5, py: 1, boxShadow: '0 10px 30px rgba(0,0,0,.28)', minWidth: 170, maxWidth: 240 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3 }}>{zone.label}</Typography>
        {status && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: TONES[status.tone].dot, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>{status.label}</Typography>
          </Stack>
        )}
        {status?.metric && <Typography sx={{ fontSize: 15, fontWeight: 750, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>{status.metric}</Typography>}
      </Box>
    </motion.div>
    </Box>
  );
};

export default PlantMap;
